import React, { useState, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { authPagesMenu } from '../../menu';
import { DropdownItem, DropdownMenu } from '../../components/bootstrap/Dropdown';
import Button from '../../components/bootstrap/Button';
import Collapse from '../../components/bootstrap/Collapse';
import { NavigationLine } from '../Navigation/Navigation';
import Icon from '../../components/icon/Icon';
import useNavigationItemHandle from '../../hooks/useNavigationItemHandle';
import AuthContext from '../../contexts/authContext';
import USERS from '../../common/data/userDummyData';
import authService from '../../services/authService';
import { ConfirmationModal } from '../../components/common';

const User = () => {
	const { userData, authUser } = useContext(AuthContext);

	const navigate = useNavigate();
	const handleItem = useNavigationItemHandle();

	const [collapseStatus, setCollapseStatus] = useState<boolean>(false);
	const [isLogoutModalOpen, setIsLogoutModalOpen] = useState<boolean>(false);
	const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

	const handleConfirmLogout = async () => {
		setIsLoggingOut(true);
		try {
			await authService.logout();
		} finally {
			setIsLoggingOut(false);
			setIsLogoutModalOpen(false);
			// A full navigation prevents stale permission guards or in-flight page
			// requests from racing the logout and redirecting to the protected 404 page.
			window.location.replace(`/${authPagesMenu.login.path}`);
		}
	};

	const { t } = useTranslation(['translation', 'menu']);

	const displayName = authUser?.name || userData?.name || 'Super Admin';
	const displayRole = authUser?.role?.name || userData?.position || 'Super Admin';
	const avatarSrc = userData?.src || USERS.JOHN.src;
	const avatarSrcSet = userData?.srcSet || USERS.JOHN.srcSet;

	return (
		<>
			<div
				className={classNames('user', { open: collapseStatus })}
				role='presentation'
				onClick={() => setCollapseStatus(!collapseStatus)}>
				<div className='user-avatar'>
					<img
						srcSet={avatarSrcSet || avatarSrc}
						src={avatarSrc}
						alt={`${displayName} Avatar`}
						width={128}
						height={128}
					/>
				</div>
				<div className='user-info'>
					<div className='user-name d-flex align-items-center'>
						{displayName}
						<Icon icon='Verified' className='ms-1' color='info' />
					</div>
					<div className='user-sub-title'>{displayRole}</div>
				</div>
			</div>
			<DropdownMenu>
				<DropdownItem>
					<Button icon='Person' onClick={() => navigate('/admin/profile')}>
						Profile
					</Button>
				</DropdownItem>
			</DropdownMenu>

			<Collapse isOpen={collapseStatus} className='user-menu'>
				<nav aria-label='aside-bottom-user-menu'>
					<div className='navigation'>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								navigate('/admin/profile');
								// @ts-ignore
								handleItem();
							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='Person' className='navigation-icon' />
									<span className='navigation-text'>Profile</span>
								</span>
							</span>
						</div>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								window.dispatchEvent(new Event('stichmaster:replay-intro'));
								setCollapseStatus(false);
								// @ts-ignore
								handleItem();
							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='HelpOutline' className='navigation-icon' />
									<span className='navigation-text'>Take a tour</span>
								</span>
							</span>
						</div>
					</div>
				</nav>
				<NavigationLine />
				<nav aria-label='aside-bottom-user-menu-2'>
					<div className='navigation'>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								setIsLogoutModalOpen(true);



							}}>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon icon='Logout' className='navigation-icon' />
									<span className='navigation-text'>
										{t('menu:Logout') as ReactNode}
									</span>
								</span>
							</span>
						</div>
					</div>
				</nav>
			</Collapse>

			<ConfirmationModal
				isOpen={isLogoutModalOpen}
				setIsOpen={setIsLogoutModalOpen}
				title='Confirmation Alert!'
				message='Are you sure you want to log out?'
				confirmText='Yes'
				cancelText='No'
				loadingText='Logging out...'
				isLoading={isLoggingOut}
				onConfirm={handleConfirmLogout}
				onCancel={() => {
					setIsLogoutModalOpen(false);
				}}
			/>
		</>
	);
};

export default User;
